/**
 * OrgScopeRibbon — thin bar rendered above every org-admin screen to tell
 * the fleet_manager "you're in corporate-admin context, not consumer host".
 *
 * Visually distinct from the consumer UI: neutral purple-tinted background,
 * icon + org legal name + "Modo flota" label, no brand coral. Keeps users
 * oriented while navigating between `/hosting/*` (consumer) and
 * `/hosting/organization/*` (admin) surfaces.
 */
import React from 'react'
import { View, Text } from 'react-native'
import { Building2, ShieldCheck } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../lib/hooks/use-theme'
import { useBreakpoint } from '../../lib/hooks/use-breakpoint'
import { useSemanticColors, SPACING, RADIUS } from '../../lib/theme/tokens'
import { FONTS, COLORS } from '../../lib/brand'

interface OrgScopeRibbonProps {
  orgName: string
  isVerified?: boolean
}

export function OrgScopeRibbon({ orgName, isVerified = false }: OrgScopeRibbonProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const { isDesktop } = useBreakpoint()
  const colors = useSemanticColors(isDark)

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.sm,
        // On desktop the parent ScreenContainer already provides the
        // outer padding + centering — the ribbon should span the full
        // centered column. Mobile keeps the tighter inset.
        marginHorizontal: isDesktop ? 0 : SPACING.lg,
        marginTop: SPACING.xs,
        marginBottom: SPACING.sm,
        borderRadius: RADIUS.md,
        backgroundColor: isDark ? 'rgba(45, 42, 94, 0.4)' : `${COLORS.purple.DEFAULT}10`,
        borderWidth: 1,
        borderColor: isDark ? 'rgba(255,255,255,0.06)' : `${COLORS.purple.DEFAULT}22`,
      }}
    >
      <Building2 size={14} color={COLORS.purple.DEFAULT} strokeWidth={2} />
      <Text
        style={{
          flex: 1,
          fontSize: 12,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
          letterSpacing: 0.1,
        }}
        numberOfLines={1}
      >
        {orgName}
      </Text>
      {isVerified && <ShieldCheck size={12} color={colors.success} strokeWidth={2} />}
      <View
        style={{
          paddingHorizontal: SPACING.sm,
          paddingVertical: 2,
          borderRadius: RADIUS.full,
          backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(45,42,94,0.08)',
        }}
      >
        <Text
          style={{
            fontSize: 12,
            fontFamily: FONTS.bodyBold,
            color: colors.textBrand,
            textTransform: 'uppercase',
            letterSpacing: 0.8,
          }}
        >
          {t('org.fleet_mode')}
        </Text>
      </View>
    </View>
  )
}
